import React, { useCallback, useEffect, useRef } from 'react'
import { useAppSelector } from 'reduxTyped'
import { Popover } from '@manychat/manyui'

import { BlockStatsViewer } from 'common/builder/components/BlockStatsViewer/BlockStatsViewer'
import { ButtonStatsViewer } from 'common/builder/components/ButtonStatsViewer/ButtonStatsViewer'
import app from 'common/builder/components/chart/FlowChart/app'
import EntityType from 'common/builder/constants/EntityType'
import { getById } from 'common/builder/selectors/builder/entitySelectors'

import { usePopoverPosition } from './hooks/usePopoverPosition'

import cm from './StatsPopover.module.css'

interface StatsPopoverV2Props {
  builderId: string
  entityId: string | null
  onClose: () => void
}

export const StatsPopover = React.memo(function StatsPopover({
  builderId,
  entityId,
  onClose,
}: StatsPopoverV2Props) {
  const entity = useAppSelector((state) => (entityId ? getById(state, builderId, entityId) : null))

  const closePopover = useCallback(() => {
    if (entityId) {
      onClose()
    }
  }, [entityId, onClose])

  useEffect(() => {
    app.on('transform', closePopover)

    return () => {
      app.removeListener('transform', closePopover)
    }
  }, [closePopover])

  const { position } = usePopoverPosition({ entityId })

  const anchor = useRef<HTMLDivElement | null>(null)

  if (!entity) {
    return null
  }

  return (
    <div
      style={{ top: position.top + 'px', left: position.left + 'px' }}
      ref={anchor}
      className={cm.anchor}
    >
      <Popover
        at="right"
        arrow
        anchor={anchor.current}
        open
        onRequestClose={closePopover}
        useLayerForClickAway
      >
        {entity.entity === EntityType.BUTTON && (
          <ButtonStatsViewer builderId={builderId} button={entity} />
        )}

        {entity.entity === EntityType.BLOCK && (
          <BlockStatsViewer builderId={builderId} block={entity} />
        )}
      </Popover>
    </div>
  )
})
